import React, { useState } from "react";
import { Alert, Form, Modal } from "react-bootstrap";
import axios from "axios";
import useAxiosData from "~/shared/use-axios-data";
import useModal from "~/shared/UseModal";
import DataTable from "~/shared/DataTable";
import TextInput from "~/shared/form/TextInput";
import ControlledForm from "~/shared/form/ControlledForm";
import FormResponse from "~/shared/form/FormResponse";
import FormButton from "~/shared/form/FormButton";
import FormSection from "~/shared/form/FormSection";
import { useForm } from "react-hook-form";
import { localeFormatDate, readErrors } from "~/shared/utils";
import FileInput from "~/shared/form/FileInput";
import { PDFDocument } from "pdf-lib";
import AlertBox from "~/shared/AlertBox";

const ELIGIBILITY_FORM_FIELD_NAMES = ["lastname", "firstname", "mi", "ssn1", "ssn2", "ssn3", "gendermale", "genderfemale", "mmdob", "dddob", "yydob", "fostery", "fostern", "fosterstate", "citizeny", "citizenn", "garesy", "garesn", "caty", "catn", "ya517", "yaover18", "ya1819", "tanfy", "tanfn", "snapy", "snapn", "medicaly", "medicaln", "lunchy", "lunchn", "peachcarey", "peachcaren", "familyunitsize", "grossincome", "grossmonthlyincome", "namefirstmiddleandlastrow1", "dateofbirthrow1", "incomesourcerow1", "amountgrossmonthlyincomerow1", "howoftenreceivedrow1", "namefirstmiddleandlastrow2", "relationshiprow2", "dateofbirthmmddyyrow2", "incomesourcerow2", "amountgrossmonthlyincomerow2", "howoftenreceivedrow2", "namefirstmiddleandlastrow3", "relationshiprow3", "dateofbirthmmddyyrow3", "incomesourcerow3", "amountgrossmonthlyincomerow3", "howoftenreceivedrow3", "namefirstmiddleandlastrow4", "relationshiprow4", "dateofbirthmmddyyrow4", "incomesourcerow4", "amountgrossmonthlyincomerow4", "howoftenreceivedrow4", "namefirstmiddleandlastrow5", "relationshiprow5", "dateofbirthmmddyyrow5", "incomesourcerow5", "amountgrossmonthlyincomerow5", "howoftenreceivedrow5", "namefirstmiddleandlastrow6", "relationshiprow6", "dateofbirthmmddyyrow6", "incomesourcerow6", "amountgrossmonthlyincomerow6", "howoftenreceivedrow6", "namefirstmiddleandlastrow7", "relationshiprow7", "dateofbirthmmddyyrow7", "incomesourcerow7", "amountgrossmonthlyincomerow7", "howoftenreceivedrow7", "parentfullname", "parentcity", "parentstate", "zipcode", "parentaddress", "homephone", "workphone", "cellphone", "parentprintedname", "date", "parentcaregiverguardiansignature", "date_2", "totalincome", "householdsize", "every2weeks", "twicemonthly", "monthly", "totalconvertedannualincome", "authorizedprogramstaffsignature", "title", "date_3"];

export default function ManagePrograms() {
    const [programs, status, setPrograms] = useAxiosData(() => axios.get("programs?all=Y"), []);
    const [showModal, setShowModal] = useState(false);
    const [editingProgram, setEditingProgram] = useState(null);
    const [showArchived, setShowArchived] = useState(false);
    const { ModalDialog, showAlert, showConfirm } = useModal();
    const formHook = useForm();

    function onSuccess(response, _, data) {
        setShowModal(false);

        if (editingProgram) {
            setPrograms(programs.map(p => p._id === editingProgram._id ? { ...p, ...data } : p));
        } else {
            setPrograms([...programs, { ...data, ...response }]);
        }

        // Wait for modal to fade out
        setTimeout(() => formHook.reset(undefined, { keepErrors: true }), 150);
    }

    async function tryEndpoint(fn, context) {
        try {
            await fn();
        } catch (error) {
            context?.setLoading(false);
            const errors = readErrors(error);
            await showAlert("Error", errors.length > 1 ? errors.map(e => <li className="mx-4 mb-1">{e}</li>) : errors[0]);
            return false;
        }

        return true;
    }

    async function deleteProgram(program, context) {
        if (!await showConfirm("Delete Program", `Are you sure you want to delete ${program.name}?`)) {
            return;
        }

        context.setLoading(true);

        if (!await tryEndpoint(() => axios.post(`programs/delete/${program._id}`), context)) {
            return;
        }

        setPrograms(programs => programs.filter(p => p._id !== program._id));
    }

    async function archiveProgram(program, context) {
        if (!await showConfirm("Archive Program", `Are you sure you want to archive ${program.name}?`)) {
            return;
        }

        context.setLoading(true);

        if (!await tryEndpoint(() => axios.post(`programs/archive/${program._id}`), context)) {
            return;
        }

        program.archived = true;
        setPrograms(programs => [...programs]);
    }

    async function unarchiveProgram(program, context) {
        if (!await showConfirm("Unarchive Program", `Are you sure you want to unarchive ${program.name}?`)) {
            return;
        }

        context.setLoading(true);
        await axios.post(`programs/unarchive/${program._id}`);
        program.archived = false;
        setPrograms(programs => [...programs]);
    }

    function editProgram(program) {
        formHook.setValue("name", program.name);
        formHook.setValue("url", program.url);
        formHook.setValue("start", program.start.split('T')[0]);
        formHook.setValue("end", program.end.split('T')[0]);
        formHook.setValue("eligibilityForm", program.eligibilityForm ? { name: program.eligibilityForm.name } : null);

        setEditingProgram(program);
        setShowModal(true);
    }

    function getProgramActions(program) {
        return <>
            <DataTable.Action onClick={() => editProgram(program)}>Edit</DataTable.Action>
            <DataTable.Action onClick={(_, context) => (program.archived ? unarchiveProgram : archiveProgram)(program, context)} manualSpinner>{program.archived ? "Unarchive" : "Archive"}</DataTable.Action>
            <DataTable.Action onClick={(_, context) => deleteProgram(program, context)} manualSpinner>Delete</DataTable.Action>
        </>;
    }

    async function validateEligibilityForm(file) {
        try {
            const pdf = await PDFDocument.load(await file.arrayBuffer());
            const form = pdf.getForm();
            const fields = form.getFields().map(f => f.getName().toLowerCase().replace(/[ /]/g, ""));

            if (!ELIGIBILITY_FORM_FIELD_NAMES.every(f => fields.includes(f))) {
                return "This PDF does not match the required format for an eligibility form.";
            }
        } catch {
            return "Invalid PDF form.";
        }
    }

    return (
        <section>
            {ModalDialog}

            <Form.Check label="Show Archived" className="d-flex align-items-center gap-2 mb-1 *:first:mt-0" onChange={e => setShowArchived(e.target.checked)} />
            <DataTable data={programs.filter(p => showArchived || !p.archived)} loading={status === "loading"} paginate>
                <DataTable.Column sortId="name" value={({ name, url, archived }) => <a href={url || undefined} target="_blank" rel="noopener noreferrer">{archived ? <i>{name}</i> : name}</a>} sortable>Program Name</DataTable.Column>
                <DataTable.Column id="start" format={localeFormatDate} sortable>Start Date</DataTable.Column>
                <DataTable.Column id="end" format={localeFormatDate} sortable>End Date</DataTable.Column>
                <DataTable.Column value={getProgramActions}>Actions</DataTable.Column>
                <DataTable.Message condition={status === "error"}>
                    <AlertBox type="error">An unknown error occurred. Please try again later.</AlertBox>
                </DataTable.Message>
                <DataTable.Message condition={programs.filter(p => showArchived || !p.archived).length === 0}>
                    <AlertBox type="info">No programs found.</AlertBox>
                </DataTable.Message>
            </DataTable>

            <Modal show={showModal} onHide={() => setShowModal(false)} className="fixed-size-modal">
                <Modal.Header closeButton>
                    <Modal.Title>{editingProgram ? "Edit Program" : "Create New Program"}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <ControlledForm hook={formHook} post={editingProgram ? `programs/update/${editingProgram._id}` : "programs/create"} onSuccess={onSuccess}>
                        <div className="flex flex-col gap-3">
                            <TextInput label="Program Name" id="name" required />
                            <TextInput label="Hyperlink" id="url" preset="url" />
                            <FormSection row wrap gap={1}>
                                <TextInput label="Start Date" id="start" type="date" required />
                                <TextInput label="End Date" id="end" type="date" required />
                            </FormSection>
                            <FileInput id="eligibilityForm" label="Eligibility Form Override" extensions={["PDF"]} validate={validateEligibilityForm} />
                        </div>
                        <FormResponse />
                        {editProgram ? <div className="d-flex flex-wrap justify-content-center gap-5 mt-4">
                            <FormButton legacy className="mx-0" submit>Save</FormButton>
                            <FormButton legacy className="mx-0" onClick={() => setShowModal(false)}>Cancel</FormButton>
                        </div> : <FormButton legacy submit />}
                    </ControlledForm>
                </Modal.Body>
            </Modal>

            <button className="large-button mt-3" onClick={() => (setEditingProgram(null), formHook.reset(), setShowModal(true))} disabled={status !== "success"}>Create Program</button>
        </section>
    );
}